// src/modules/app-views/invoices/pages/Document/tabs/PreviewTab/IssuerSection.jsx
// *******************************************************************************
// ISSUER SECTION - DANFE NFC-e
// *******************************************************************************
// Displays issuer (emitente) data: company name, CNPJ, IE, address.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography } from 'antd';
import { parseCnpjToDisplay } from 'utils/helpers';

const { Text, Title } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const IssuerSection = ({ data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const issuer = data?.issuer || data?.issuerEntity || {};

  // Extract issuer data with fallbacks
  const companyName = issuer.name || issuer.razaoSocial || '';
  const tradeName = issuer.tradeName || issuer.nomeFantasia || '';
  const cnpj = issuer.cnpj || '';
  const ie = issuer.stateRegistration || issuer.ie || '';

  // Build address string
  const address = issuer.address || {};
  const addressLine1 = buildAddressLine1(address);
  const addressLine2 = buildAddressLine2(address);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="danfe-section danfe-issuer">
      {/* Company Name */}
      <Title level={5} style={{ margin: 0, textAlign: 'center', fontSize: 14 }}>
        {tradeName || companyName}
      </Title>

      {/* Razão Social (if different from trade name) */}
      {tradeName && companyName && tradeName !== companyName && (
        <Text style={{ display: 'block', textAlign: 'center', fontSize: 11 }}>{companyName}</Text>
      )}

      {/* CNPJ and IE */}
      <div style={{ textAlign: 'center', marginTop: 4 }}>
        <Text style={{ fontSize: 11 }}>
          CNPJ: {parseCnpjToDisplay(cnpj) || cnpj}
          {ie && ` | IE: ${ie}`}
        </Text>
      </div>

      {/* Address */}
      {addressLine1 && (
        <Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center', fontSize: 10, marginTop: 2 }}
        >
          {addressLine1}
        </Text>
      )}

      {addressLine2 && (
        <Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center', fontSize: 10 }}
        >
          {addressLine2}
        </Text>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default IssuerSection;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build first address line (street, number, complement)
 * @param {Object} address - Address object
 * @returns {string} Address line 1
 */
function buildAddressLine1(address) {
  const parts = [];

  if (address.address1 || address.street) {
    parts.push(address.address1 || address.street);
  }

  if (address.number) {
    parts.push(address.number);
  }

  if (address.address2 || address.complement) {
    parts.push(address.address2 || address.complement);
  }

  return parts.join(', ');
}

/**
 * Build second address line (neighborhood, city, state, zip)
 * @param {Object} address - Address object
 * @returns {string} Address line 2
 */
function buildAddressLine2(address) {
  const parts = [];

  if (address.neighborhood || address.district) {
    parts.push(address.neighborhood || address.district);
  }

  const city = address.city;
  const state = address.region || address.state;

  if (city && state) {
    parts.push(`${city}/${state}`);
  } else if (city) {
    parts.push(city);
  }

  if (address.zip || address.zipCode || address.postalCode) {
    parts.push(`CEP: ${address.zip || address.zipCode || address.postalCode}`);
  }

  return parts.join(' - ');
}
